import {useState} from "react";
import {useTranslation} from "react-i18next";
import {Col, message, Row} from "antd";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";

import {updateUser, updateUserPass} from "../../server/User";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {updateUserInfo} from "../../features/user/userSlice";

export const EditUserInfo = () => {
  const {t} = useTranslation();
  const userState = useAppSelector((state: any) => state.user);
  const [messageApi, contextHolder] = message.useMessage();
  const dispatch = useAppDispatch();

  const EditInfoSchema = Yup.object().shape({
    family_name: Yup.string().required(t("Last Name Field is required")),
    given_name: Yup.string().required(t("First Name Field is required")),
  });

  const EditPasswordSchema = Yup.object().shape({
    current_password: Yup.string()
      .min(8, t("Password must be longer than 8 characters"))
      .matches(
        /^(?=.*[A-Z])/,
        t("Must contain at least one uppercase character")
      )
      .required(t("Password field is required")),
    new_password: Yup.string()
      .min(8, t("Password must be longer than 8 characters"))
      .matches(
        /^(?=.*[A-Z])/,
        t("Must contain at least one uppercase character")
      )
      .required(t("Confirm Password field is required")),
    confirm_password: Yup.string()
      .required(t("Confirm New Password field is required"))
      .oneOf(
        [Yup.ref("new_password")],
        t("Please make sure the passwords match")
      ),
  });

  const editInfoSuccess = () => {
    messageApi.success({
      content: t("Edit User Success!"),
    });
  };

  const editInfoError = () => {
    messageApi.error({
      content: t("Edit User Failed!"),
    });
  };

  const changePwdSuccess = () => {
    messageApi.success({
      content: t("Password changed success!"),
    });
  };

  const changePwdError = () => {
    messageApi.error({
      content: t("Password changed failed!"),
    });
  };

  const handleUpdateUserPass = (userBlob: any) => {
    updateUserPass(userBlob)
      .then((res) => {
        const data = res.data;
        if (data.code === 0) {
          changePwdSuccess();
        } else {
          changePwdError();
        }
      })
      .catch((res) => {
        console.log(res);
        editInfoError();
      });
  };

  const handleUpdateUser = (updateType: string, userBlob: any) => {
    updateUser(userState.user_id, updateType, userBlob)
      .then((res) => {
        const data = res.data;
        if (data.msg == "success") {
          editInfoSuccess();
          dispatch(updateUserInfo(data.data));
        }

        if (updateType === "password") {
          if (data.msg === "Password changed successfully") {
            changePwdSuccess();
          }

          if (data.status === -1) {
            changePwdError();
          }
        }
      })
      .catch((res) => {
        console.log(res);
        editInfoError();
      });
  };

  const EditProfileForm = () => {
    return (
      <div className="form">
        <h2>{t("Edit User")}</h2>
        <Formik
          validateOnMount={true}
          enableReinitialize={true}
          validationSchema={EditInfoSchema}
          initialValues={{}}
          onSubmit={(values: any) => {
            handleUpdateUser("profile", {
              ...values,
              name: values.family_name + " " + values.given_name,
            });
          }}
        >
          {({errors, touched}) => (
            <Form>
              <Row gutter={[0, 24]}>
                <Col span={24}>
                  <div className="label-wrapper">
                    <label>{t("First Name")}</label>
                    <Field name="given_name" type="text"/>
                    <div className="form-error-msg">
                      <ErrorMessage name="given_name"/>
                    </div>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="label-wrapper">
                    <label>{t("Last Name")}</label>
                    <Field name="family_name" type="text"/>
                    <div className="form-error-msg">
                      <ErrorMessage name="family_name"/>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row gutter={[0, 24]}>
                <Col span={24}>
                  <button className="primary-btn" type="submit">
                    {t("Edit User")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  const EditPasswordForm = () => {
    return (
      <div className="form">
        <h2>{t("Edit Password")}</h2>
        <Formik
          validateOnMount={true}
          enableReinitialize={true}
          validationSchema={EditPasswordSchema}
          initialValues={{}}
          onSubmit={(values: any) => {
            handleUpdateUserPass({
              ...values,
            });
          }}
        >
          {({errors, touched}) => (
            <Form>
              <Row gutter={[0, 24]}>
                <Col span={8}>
                  <div className="label-wrapper">
                    <label>{t("Old Password")}</label>
                    <Field name="current_password" type="password"/>
                    <div className="form-error-msg">
                      <ErrorMessage name="current_password"/>
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="label-wrapper">
                    <label>{t("New Password")}</label>
                    <Field name="new_password" type="password"/>
                    <div className="form-error-msg">
                      <ErrorMessage name="new_password"/>
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="label-wrapper">
                    <label>{t("Confirm Password")}</label>
                    <Field name="confirm_password" type="password"/>
                    <div className="form-error-msg">
                      <ErrorMessage name="confirm_password"/>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row gutter={[0, 24]}>
                <Col span={12}>
                  <button className="primary-btn" type="submit">
                    {t("Update Password")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <EditProfileForm/>
      <EditPasswordForm/>
    </>
  );
};
